import { useMutation, useQuery } from "./urqlHooks";

const MASTER_STRUCTURE_FIELDS = `
  id
  companyId
  type
  name
  order
  accounts {
    key
    label
    name
    children {
      key
      label
      name
      children {
        key
        label
        name
        children {
          key
          label
          name
        }
      }
    }
  }
`;

const GET_MASTER_STRUCTURES = `
  query GET_MASTER_STRUCTURES($companyId: String!) {
    masterStructures(companyId: $companyId) {
      ${MASTER_STRUCTURE_FIELDS}
    }
  }
`;

export const GetMasterStructures = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_MASTER_STRUCTURES,
    variables,
    requestPolicy: "network-only",
    pause: !variables.companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_MASTER_STRUCTURE_MAPPINGS = `
  query GET_MASTER_STRUCTURE_MAPPINGS($companyId: String!, $sourceType: MasterMappingSourceType) {
    masterStructureMappings(companyId: $companyId, sourceType: $sourceType) {
      id
      structureType
      sourceType
      sourceKey
      masterKey
      weight
    }
  }
`;

export const GetMasterStructureMappings = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_MASTER_STRUCTURE_MAPPINGS,
    variables,
    requestPolicy: "network-only",
    pause: !variables.companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const CREATE_MASTER_ACCOUNT = `
  mutation CREATE_MASTER_ACCOUNT(
    $companyId: String!
    $type: MasterStructureType!
    $parentKey: String!
    $name: String!
  ) {
    createMasterAccount(companyId: $companyId, type: $type, parentKey: $parentKey, name: $name) {
      ${MASTER_STRUCTURE_FIELDS}
    }
  }
`;

export const CreateMasterAccount = () => {
  const [result, executeMutation] = useMutation(CREATE_MASTER_ACCOUNT);
  return { ...result, executeMutation };
};

const UPDATE_MASTER_ACCOUNT = `
  mutation UPDATE_MASTER_ACCOUNT(
    $companyId: String!
    $type: MasterStructureType!
    $key: String!
    $name: String!
  ) {
    updateMasterAccount(companyId: $companyId, type: $type, key: $key, name: $name) {
      ${MASTER_STRUCTURE_FIELDS}
    }
  }
`;

export const UpdateMasterAccount = () => {
  const [result, executeMutation] = useMutation(UPDATE_MASTER_ACCOUNT);
  return { ...result, executeMutation };
};

const DELETE_MASTER_ACCOUNT = `
  mutation DELETE_MASTER_ACCOUNT($companyId: String!, $type: MasterStructureType!, $key: String!) {
    deleteMasterAccount(companyId: $companyId, type: $type, key: $key) {
      ${MASTER_STRUCTURE_FIELDS}
    }
  }
`;

export const DeleteMasterAccount = () => {
  const [result, executeMutation] = useMutation(DELETE_MASTER_ACCOUNT);
  return { ...result, executeMutation };
};

const REORDER_MASTER_ACCOUNTS = `
  mutation REORDER_MASTER_ACCOUNTS(
    $companyId: String!
    $type: MasterStructureType!
    $parentKey: String
    $orderedKeys: [String!]!
  ) {
    reorderMasterAccounts(
      companyId: $companyId
      type: $type
      parentKey: $parentKey
      orderedKeys: $orderedKeys
    ) {
      ${MASTER_STRUCTURE_FIELDS}
    }
  }
`;

export const ReorderMasterAccounts = () => {
  const [result, executeMutation] = useMutation(REORDER_MASTER_ACCOUNTS);
  return { ...result, executeMutation };
};

const UPDATE_MASTER_STRUCTURE = `
  mutation UPDATE_MASTER_STRUCTURE(
    $companyId: String!
    $type: MasterStructureType!
    $input: UpdateMasterStructureInput!
  ) {
    updateMasterStructure(companyId: $companyId, type: $type, input: $input) {
      ${MASTER_STRUCTURE_FIELDS}
    }
  }
`;

export const UpdateMasterStructure = () => {
  const [result, executeMutation] = useMutation(UPDATE_MASTER_STRUCTURE);
  return { ...result, executeMutation };
};

const ASSIGN_MASTER_ACCOUNT = `
  mutation ASSIGN_MASTER_ACCOUNT($companyId: String!, $input: AssignMasterAccountInput!) {
    assignMasterAccount(companyId: $companyId, input: $input) {
      id
      structureType
      sourceType
      sourceKey
      masterKey
      weight
    }
  }
`;

export const AssignMasterAccount = () => {
  const [result, executeMutation] = useMutation(ASSIGN_MASTER_ACCOUNT);
  return { ...result, executeMutation };
};

const BULK_ASSIGN_MASTER_ACCOUNTS = `
  mutation BULK_ASSIGN_MASTER_ACCOUNTS($companyId: String!, $items: [AssignMasterAccountInput!]!) {
    bulkAssignMasterAccounts(companyId: $companyId, items: $items) {
      assigned
      unassigned
      errors
    }
  }
`;

export const BulkAssignMasterAccounts = () => {
  const [result, executeMutation] = useMutation(BULK_ASSIGN_MASTER_ACCOUNTS);
  return { ...result, executeMutation };
};

const MOVE_MASTER_ACCOUNT = `
  mutation MOVE_MASTER_ACCOUNT(
    $companyId: String!
    $type: MasterStructureType!
    $key: String!
    $newParentKey: String!
    $unassignTarget: Boolean
  ) {
    moveMasterAccount(
      companyId: $companyId
      type: $type
      key: $key
      newParentKey: $newParentKey
      unassignTarget: $unassignTarget
    ) {
      newKey
      unassigned
      structure { type }
    }
  }
`;

export const MoveMasterAccount = () => {
  const [result, executeMutation] = useMutation(MOVE_MASTER_ACCOUNT);
  return { ...result, executeMutation };
};

const IMPORT_MASTER_STRUCTURES = `
  mutation IMPORT_MASTER_STRUCTURES(
    $companyId: String!
    $sourceCompanyId: String!
    $types: [MasterStructureType!]!
  ) {
    importMasterStructures(
      companyId: $companyId
      sourceCompanyId: $sourceCompanyId
      types: $types
    ) {
      structures
      accounts
      unassigned
    }
  }
`;

export const ImportMasterStructures = () => {
  const [result, executeMutation] = useMutation(IMPORT_MASTER_STRUCTURES);
  return { ...result, executeMutation };
};
